'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { CAPACITY, CAPACITY_MONTHS, CAPACITY_PLANNED_HOURS, teamMember } from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, ColumnLabel, HeaderStat } from './app-screen'
import styles from './capacity-visual.module.css'
import { useScenario } from './use-scenario'

// Occupancy per colleague, straight off the shared capacity figures the assistant also answers
// from. `pct` is each row's BASE value; a row with `spikesTo` rises to it mid-loop, so the resolved
// rest state is the post-spike picture.
const EMPLOYEES = CAPACITY.map((row) => ({ ...row, ...teamMember(row.member) }))

/**
 * A month running over capacity, and the work moving off it.
 *
 * All twenty-three bars used to drain to zero and refill on every pass, which put the loop's time
 * into a chart rebuilding itself rather than into the one month that matters — and while they grew,
 * each row's figure sat at its final value beside a bar still climbing, so number and picture told
 * different stories. Nothing drains now. The bars hold their values and the story is mei going over
 * and coming back down.
 *
 * The redistribution lands on the wrap: returning to beat 0 IS the work moving off mei and onto the
 * months with room, which is why there is no separate beat for it — one would show exactly what
 * beat 0 already shows.
 */
const BEAT_HOLDS = [
  2000, // 0  the year in balance — mei at 92%, jun and jul carrying what moved off it
  900, //  1  DA's own workload climbs to 96%
  2000, // 2  mei goes to 124% — vol. Held: this is the beat the whole chart exists for, and the
  //         one thing on screen a viewer needs time to read.
] as const

const REST_BEAT = 0

const EMPLOYEE_SPIKE_FROM = 1
const MONTH_SPIKE_FROM = 2
const TOAST_WINDOW: readonly [number, number] = [2, 3]

/** Months that absorb mei's overflow once it is redistributed — jun and jul, which have the room. */
const ABSORBS: Record<number, number> = { 5: 52, 6: 49 }

/**
 * Below this, a month has room worth offering. It is the same line `capacityHint` drew, applied to
 * the track behind the bar instead of to a word in a fourth column.
 */
const ROOM_BELOW_PCT = 75

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive bar state. */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

/** >100% = over capacity (red), >90% = near capacity (amber), else on-track (accent). */
function barColor(pct: number) {
  if (pct > 100) return 'var(--au-late)'
  if (pct > 90) return 'var(--au-sig)'
  return 'var(--au-accent)'
}

/** A row's occupancy at a given beat: its base value until the spike beat, its peak after. */
function occupancy(row: { pct: number; spikesTo?: number }, beat: number, spikeFrom: number): number {
  return row.spikesTo !== undefined && beat >= spikeFrom ? row.spikesTo : row.pct
}

/**
 * One occupancy bar. Its width is derived from the same `pct` the figure beside it renders, with no
 * separate "has it grown yet" state — that flag is what used to let `88%` sit next to a bar a tenth
 * full.
 *
 * The unfilled part of the track is the room, and it is drawn as room: a month with capacity to
 * spare washes its remainder in the same green finished work uses, rather than leaving it the same
 * neutral grey a full month's nothing gets. Space was a word in a fourth column before, which said
 * a month had room without showing any — and said it about six months at once, none of them the
 * month the overflow actually went to. `lit` is that same wash turned up, on the two months taking
 * the work, at the moment they take it.
 */
function Bar({
  pct,
  delayMs,
  room,
  lit,
  hint,
}: {
  pct: number
  delayMs: number
  room?: boolean
  lit?: boolean
  /** "vol" or "ruimte", drawn inside the bar rather than in a column beside it. */
  hint?: string
}) {
  return (
    <div
      className={clsx(styles.track, room && styles.trackRoom, lit && styles.trackRoomLit, 'relative h-[10px] flex-1')}
    >
      <div
        className={clsx(styles.bar, 'h-full rounded-full')}
        style={{
          width: `${Math.min(pct, 100)}%`,
          background: barColor(pct),
          transitionDelay: `${delayMs}ms`,
        }}
      />
      {/* The word sits in the bar it describes: "ruimte" in the room that is left, "vol" on the fill
          that used it up. It used to have a column of its own, 30px wide and empty on nine rows out
          of twelve — a band of white down the right of the month side that the employee side had no
          counterpart for, so the two halves of the card never looked like one grid. */}
      {hint && (
        <span
          className={clsx(styles.hint, pct > 100 ? styles.hintFull : styles.hintRoom)}
          style={{ left: pct > 100 ? undefined : `${Math.min(pct, 100)}%`, right: pct > 100 ? undefined : 0 }}
        >
          {hint}
        </span>
      )}
    </div>
  )
}

/**
 * Grip card 2 — capacity overview: the eleven colleagues on the left and the twelve months of the
 * book year on the right grow in, then DA (82→96%) and May (92→124%) spike over capacity and flag
 * amber/red with a transient "redistribute" toast. Driven by `useScenario`'s 10-beat loop;
 * `beat === null` (SSR, no-JS, `prefers-reduced-motion: reduce`) renders the resolved rest state:
 * all bars at their final (post-spike) values, no toast.
 */
export function CapacityVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const toastVisible = effBeat >= TOAST_WINDOW[0] && effBeat < TOAST_WINDOW[1]

  return (
    <AppUiVisual label={t.screen.capacity.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="capacity" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.capacity.title}
            detail={t.period.currentYear}
            right={
              <span className={styles.headerSlot}>
                <span className={clsx(styles.headerStat, toastVisible && styles.headerStatOut)}>
                  <HeaderStat
                    value={`${fmt.decimal(CAPACITY_PLANNED_HOURS, 2)} ${t.word.hoursUnit}`}
                    label={t.word.planned}
                  />
                </span>
                <span className={clsx(styles.alert, toastVisible && styles.alertIn)}>{t.screen.capacity.overload}</span>
              </span>
            }
          />

          {/* Both columns give their rows `flex-1`, so the bars spread across whatever height the
              card has instead of stacking at the top and leaving the lower half empty. Eleven
              colleagues against twelve months keeps the two sides within a row of each other, so
              they still read as one grid. */}
          <div className="grid min-h-0 flex-1 grid-cols-2">
            <div className="flex min-h-0 flex-col border-r px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
              <ColumnLabel>{t.screen.capacity.perEmployee}</ColumnLabel>
              <div className="flex min-h-0 flex-1 flex-col">
                {EMPLOYEES.map((row, i) => {
                  const pct = occupancy(row, effBeat, EMPLOYEE_SPIKE_FROM)
                  return (
                    <div key={row.initials} className="flex min-h-0 flex-1 items-center gap-1.5">
                      <span
                        className="flex size-[17px] shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-white"
                        style={{ background: row.color }}
                      >
                        {row.initials}
                      </span>
                      <Bar pct={pct} delayMs={i * 40} room={pct < ROOM_BELOW_PCT} />
                      <span
                        className={clsx(
                          styles.value,
                          'w-[27px] shrink-0 text-right text-[10px] font-bold whitespace-nowrap tabular-nums',
                        )}
                        style={{ color: barColor(pct) }}
                      >
                        {pct}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex min-h-0 flex-col px-3 py-2.5">
              <ColumnLabel>{t.screen.capacity.perMonth}</ColumnLabel>
              <div className="flex min-h-0 flex-1 flex-col">
                {CAPACITY_MONTHS.map((row, i) => {
                  // While mei is over capacity the work is still on mei; once it moves, jun and
                  // jul carry it. Same hours, different month — which is what redistribution means.
                  const absorbed = ABSORBS[row.monthIndex]
                  const base = absorbed !== undefined && effBeat < MONTH_SPIKE_FROM ? absorbed : row.pct
                  const pct = occupancy({ ...row, pct: base }, effBeat, MONTH_SPIKE_FROM)
                  // Named only where naming it says something. "Vol" is an alarm and earns a word;
                  // "ruimte" earns one only on the two months about to take the work, while mei is
                  // over — everywhere else the lit track already shows the room without labelling
                  // six months at once with a fact nothing follows from.
                  const takesOverflow = absorbed !== undefined && effBeat >= MONTH_SPIKE_FROM
                  const hint = pct > 100 ? t.word.full : takesOverflow ? t.word.room : undefined
                  return (
                    <div key={row.monthIndex} className="flex min-h-0 flex-1 items-center gap-1.5">
                      <span
                        className="w-[20px] shrink-0 text-[9px] font-medium whitespace-nowrap capitalize"
                        style={{ color: 'var(--au-text2)' }}
                      >
                        {t.monthsShort[row.monthIndex]}
                      </span>
                      <Bar pct={pct} delayMs={i * 40} room={pct < ROOM_BELOW_PCT} lit={takesOverflow} hint={hint} />
                      <span
                        className={clsx(
                          styles.value,
                          'w-[27px] shrink-0 text-right text-[10px] font-bold whitespace-nowrap tabular-nums',
                        )}
                        style={{ color: barColor(pct) }}
                      >
                        {pct}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
